import { ArrowLeft, CheckCircle, Download, FileText } from 'lucide-react';
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

interface ServiceRequestFormData {
  // Request Information
  requestNumber: string;
  requestDate: string;
  projectTitle: string;
  urgencyLevel: string;
  
  // Client Information
  companyName: string;
  contactName: string;
  jobTitle: string;
  email: string;
  phone: string;
  address: string;
  
  // Service Requirements
  serviceType: string;
  projectScope: string;
  deliverables: string;
  timeline: string;
  budget: string;
  
  // Technical Specifications
  technicalRequirements: string;
  platformPreferences: string;
  integrationNeeds: string;
  performanceRequirements: string;
  
  // Terms and Conditions
  paymentTerms: string;
  deliveryTerms: string;
  warrantyRequirements: boolean;
  supportRequirements: boolean;
  trainingRequired: boolean;
  
  // Authorization
  authorizedSignature: string;
  signerTitle: string;
  signatureDate: string;
}

export default function CompleteMarketingForm() {
  const { taskId } = useParams();
  const isFromTask = taskId !== undefined;
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const [formData, setFormData] = useState<ServiceRequestFormData>({
    requestNumber: `REQ-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`,
    requestDate: new Date().toISOString().split('T')[0],
    projectTitle: 'Green Horizons Brand Relaunch', // Field 2
    urgencyLevel: '',
    companyName: '',
    contactName: '',
    jobTitle: '',
    email: '',
    phone: '',
    address: '123 Greenway Rd, San Francisco CA 94107', // Field 5f
    serviceType: '',
    projectScope: 'Concept, design & production of refreshed branding; three 15-sec video ads; full social-asset suite; influencer partnership kit; 30-day performance report.', // Field 6c
    deliverables: 'Brand Guidelines PDF; 3 × 15-sec video spots; 30 × static/vertical social assets; influencer brief deck; post-launch analytics report.', // Field 6d
    timeline: '',
    budget: '',
    technicalRequirements: 'Layered PSD/AI source files; exports in 1080×1080, 1080×1350, 1920×1080.', // Field 7a
    platformPreferences: 'Design – Figma; Review – Frame.io; PM – Asana.', // Field 7b
    integrationNeeds: 'Nightly webhook of campaign metrics to Looker Studio; ad-audience sync with Klaviyo.', // Field 7c
    performanceRequirements: 'Target 5% CTR on paid social and 20% uplift in site traffic within first 30 days.', // Field 7d
    paymentTerms: '',
    deliveryTerms: 'All final creative assets delivered 16 Sep 2025, 17:00 PDT via shared Drive & Frame.io.', // Field 8b
    warrantyRequirements: false,
    supportRequirements: false,
    trainingRequired: false,
    authorizedSignature: '',
    signerTitle: '',
    signatureDate: '',
  });

  const handleInputChange = (field: keyof ServiceRequestFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    downloadJSON();
    setIsSubmitted(true);
  };

  const downloadJSON = () => {
    const dataToDownload = {
      formData: formData,
      formType: 'Professional Services Request Form',
      source: 'CRM Marketing Forms'
    };

    const jsonString = JSON.stringify(dataToDownload, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `service-request.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-300">
      {/* PDF-like toolbar */}
      <div className="bg-gray-800 text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to={isFromTask ? "/tasks" : "/marketing/forms"} className="flex items-center text-gray-300 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-1" />
            {isFromTask ? "Back to Tasks" : "Back to Forms"}
          </Link>
          <div className="flex items-center">
            <FileText className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">Professional Services Request Form</span>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-300">Page 1 of 1</span>
          <div className="bg-gray-700 px-2 py-1 rounded text-sm">100%</div>
          <button 
            onClick={downloadJSON}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 text-sm rounded flex items-center"
          >
            <Download className="w-4 h-4 mr-1" />
            Submit
          </button>
        </div>
      </div>
      
      {/* Success Message */}
      {isSubmitted && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mx-6 mt-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-green-800">
                {isFromTask ? "Task Completed Successfully!" : "Form Submitted Successfully!"}
              </h3>
              <p className="text-sm text-green-700 mt-1">
                Your professional service request form has been submitted and downloaded as JSON.
                {isFromTask && " The task has been marked as complete."}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* PDF-like document container */}
      <div className="flex justify-center py-6">
        <div className="bg-white shadow-2xl" style={{width: '8.5in', minHeight: '11in'}}>
          <div className="border-2 border-black bg-white">
            {/* Header */}
            <div className="border-b border-black bg-white p-2">
              <div className="text-center text-sm font-bold uppercase tracking-wide">
                PROFESSIONAL SERVICES REQUEST FORM
              </div>
              <div className="flex justify-between items-center mt-2 text-xs">
                <span className="font-semibold">NOTE: ALL FIELDS MARKED WITH * ARE REQUIRED</span>
                <div className="flex gap-8 items-center">
                  <div className="flex items-center gap-2">
                    <span>REQUEST NUMBER:</span>
                    <input
                      value={formData.requestNumber}
                      readOnly
                      className="h-5 w-32 text-xs border-0 p-1 bg-blue-50 font-mono"
                    />
                  </div>
                  <span>PAGE 1 OF 1</span>
                </div>
              </div>
            </div>
            
            <div className="p-0">
              <form onSubmit={handleSubmit}>
                {/* Row 1: Basic Information */}
                <div className="grid grid-cols-4 border-b border-black">
                  <div className="border-r border-black p-2">
                    <div className="text-xs font-semibold">1. REQUEST DATE</div>
                    <input
                      value={formData.requestDate}
                      onChange={(e) => handleInputChange('requestDate', e.target.value)}
                      type="date"
                      className="h-6 w-full text-xs border-0 p-1 bg-blue-50"
                    />
                  </div>
                  <div className="border-r border-black p-2">
                    <div className="text-xs font-semibold">2. PROJECT TITLE *</div>
                    <input
                      value={formData.projectTitle}
                      onChange={(e) => handleInputChange('projectTitle', e.target.value)}
                      required
                      className="h-6 w-full text-xs border-0 p-1 bg-yellow-50"
                      placeholder="Enter project title"
                    />
                  </div>
                  <div className="border-r border-black p-2">
                    <div className="text-xs font-semibold">3. URGENCY LEVEL *</div>
                    <select
                      value={formData.urgencyLevel}
                      onChange={(e) => handleInputChange('urgencyLevel', e.target.value)}
                      required
                      className="h-6 w-full text-xs border-0 p-1 bg-yellow-50"
                    >
                      <option value="">Select urgency</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                  <div className="p-2">
                    <div className="text-xs font-semibold">4. SERVICE TYPE *</div>
                    <select
                      value={formData.serviceType}
                      onChange={(e) => handleInputChange('serviceType', e.target.value)}
                      required
                      className="h-6 w-full text-xs border-0 p-1 bg-yellow-50"
                    >
                      <option value="">Select service</option>
                      <option value="Web Development">Web Development</option>
                      <option value="Mobile App Development">Mobile App Development</option>
                      <option value="Software Development">Software Development</option>
                      <option value="Consulting Services">Consulting Services</option>
                      <option value="Design Services">Design Services</option>
                      <option value="Integrated Marketing Campaign">Integrated Marketing Campaign</option>
                    </select>
                  </div>
                </div>

                {/* Row 2: Client Information */}
                <div className="border-b border-black p-2">
                  <div className="text-xs font-semibold mb-2">5. CLIENT INFORMATION</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs mb-1">a. COMPANY NAME *</div>
                      <input
                        value={formData.companyName}
                        onChange={(e) => handleInputChange('companyName', e.target.value)}
                        required
                        className="h-6 w-full text-xs border border-gray-300 p-1 bg-yellow-50"
                        placeholder="Enter company name"
                      />
                    </div>
                    <div>
                      <div className="text-xs mb-1">b. CONTACT NAME *</div>
                      <input
                        value={formData.contactName}
                        onChange={(e) => handleInputChange('contactName', e.target.value)}
                        required
                        className="h-6 w-full text-xs border border-gray-300 p-1 bg-yellow-50"
                        placeholder="Enter contact name"
                      />
                    </div>
                    <div>
                      <div className="text-xs mb-1">c. JOB TITLE *</div>
                      <input
                        value={formData.jobTitle}
                        onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                        required
                        className="h-6 w-full text-xs border border-gray-300 p-1 bg-yellow-50"
                        placeholder="Enter job title"
                      />
                    </div>
                    <div>
                      <div className="text-xs mb-1">d. EMAIL ADDRESS *</div>
                      <input
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        type="email"
                        required
                        className="h-6 w-full text-xs border border-gray-300 p-1 bg-yellow-50"
                        placeholder="Enter email address"
                      />
                    </div>
                    <div>
                      <div className="text-xs mb-1">e. PHONE NUMBER *  (ex: 413-555-0199)</div>
                      <input
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        type="tel"
                        required
                        className="h-6 w-full text-xs border border-gray-300 p-1 bg-yellow-50"
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div>
                      <div className="text-xs mb-1">f. BUSINESS ADDRESS</div>
                      <input
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        className="h-6 w-full text-xs border border-gray-300 p-1 bg-blue-50"
                        placeholder="Enter business address"
                      />
                    </div>
                  </div>
                </div>

                {/* Row 3: Project Requirements */}
                <div className="border-b border-black p-2">
                  <div className="text-xs font-semibold mb-2">6. PROJECT REQUIREMENTS</div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-xs mb-1">a. TIMELINE *</div>
                      <select
                        value={formData.timeline}
                        onChange={(e) => handleInputChange('timeline', e.target.value)}
                        required
                        className="h-6 w-full text-xs border border-gray-300 p-1 bg-yellow-50"
                      >
                        <option value="">Select timeline</option>
                        <option value="1-2 weeks">1-2 weeks</option>
                        <option value="1 month">1 month</option>
                        <option value="2 months">2 months</option>
                        <option value="8 weeks">8 weeks</option>
                        <option value="3 months">3 months</option>
                        <option value="6 months">6 months</option>
                        <option value="12+ months">12+ months</option>
                        <option value="Ongoing">Ongoing</option>
                      </select>
                    </div>
                    <div>
                      <div className="text-xs mb-1">b. BUDGET RANGE *</div>
                      <select
                        value={formData.budget}
                        onChange={(e) => handleInputChange('budget', e.target.value)}
                        required
                        className="h-6 w-full text-xs border border-gray-300 p-1 bg-yellow-50"
                      >
                        <option value="">Select budget</option>
                        <option value="Under $5,000">Under $5,000</option>
                        <option value="$5,000 - $15,000">$5,000 - $15,000</option>
                        <option value="$15,000 - $50,000">$15,000 - $50,000</option>
                        <option value="$35,000 – $50,000 USD">$35,000 – $50,000 USD</option>
                        <option value="$50,000 - $100,000">$50,000 - $100,000</option>
                        <option value="Over $100,000">Over $100,000</option>
                      </select>
                    </div>
                  </div>
                  <div className="mb-4">
                    <div className="text-xs mb-1">c. PROJECT SCOPE *</div>
                    <textarea
                      value={formData.projectScope}
                      onChange={(e) => handleInputChange('projectScope', e.target.value)}
                      required
                      rows={3}
                      className="w-full text-xs border border-gray-300 p-1 bg-yellow-50"
                      placeholder="Describe the project scope and objectives..."
                    />
                  </div>
                  <div>
                    <div className="text-xs mb-1">d. DELIVERABLES *</div>
                    <textarea
                      value={formData.deliverables}
                      onChange={(e) => handleInputChange('deliverables', e.target.value)}
                      required
                      rows={3}
                      className="w-full text-xs border border-gray-300 p-1 bg-yellow-50"
                      placeholder="List expected deliverables..."
                    />
                  </div>
                </div>

                {/* Row 4: Technical Specifications */}
                <div className="border-b border-black p-2">
                  <div className="text-xs font-semibold mb-2">7. TECHNICAL SPECIFICATIONS</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs mb-1">a. TECHNICAL REQUIREMENTS</div>
                      <textarea
                        value={formData.technicalRequirements}
                        onChange={(e) => handleInputChange('technicalRequirements', e.target.value)}
                        rows={3}
                        className="w-full text-xs border border-gray-300 p-1 bg-blue-50"
                        placeholder="Specify technical requirements..."
                      />
                    </div>
                    <div>
                      <div className="text-xs mb-1">b. PLATFORM PREFERENCES</div>
                      <textarea
                        value={formData.platformPreferences}
                        onChange={(e) => handleInputChange('platformPreferences', e.target.value)}
                        rows={3}
                        className="w-full text-xs border border-gray-300 p-1 bg-blue-50"
                        placeholder="Preferred platforms or technologies..."
                      />
                    </div>
                    <div>
                      <div className="text-xs mb-1">c. INTEGRATION NEEDS</div>
                      <textarea
                        value={formData.integrationNeeds}
                        onChange={(e) => handleInputChange('integrationNeeds', e.target.value)}
                        rows={3}
                        className="w-full text-xs border border-gray-300 p-1 bg-blue-50"
                        placeholder="Integration requirements..."
                      />
                    </div>
                    <div>
                      <div className="text-xs mb-1">d. PERFORMANCE REQUIREMENTS</div>
                      <textarea
                        value={formData.performanceRequirements}
                        onChange={(e) => handleInputChange('performanceRequirements', e.target.value)}
                        rows={3}
                        className="w-full text-xs border border-gray-300 p-1 bg-blue-50"
                        placeholder="Performance expectations..."
                      />
                    </div>
                  </div>
                </div>

                {/* Row 5: Terms and Conditions */}
                <div className="border-b border-black p-2">
                  <div className="text-xs font-semibold mb-2">8. TERMS AND CONDITIONS</div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-xs mb-1">a. PAYMENT TERMS</div>
                      <select
                        value={formData.paymentTerms}
                        onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                        className="h-6 w-full text-xs border border-gray-300 p-1 bg-green-50"
                      >
                        <option value="Net 15">Net 15</option>
                        <option value="Net 30">Net 30</option>
                        <option value="Net 60">Net 60</option>
                        <option value="50% upfront">50% upfront, 50% on completion</option>
                        <option value="Monthly">Monthly payments</option>
                      </select>
                    </div>
                    <div>
                      <div className="text-xs mb-1">b. DELIVERY TERMS</div>
                      <input
                        value={formData.deliveryTerms}
                        onChange={(e) => handleInputChange('deliveryTerms', e.target.value)}
                        className="h-6 w-full text-xs border border-gray-300 p-1 bg-green-50"
                        placeholder="Specify delivery terms..."
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.warrantyRequirements}
                        onChange={(e) => handleInputChange('warrantyRequirements', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-xs">Warranty/Guarantee Required</span>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.supportRequirements}
                        onChange={(e) => handleInputChange('supportRequirements', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-xs">Ongoing Support Required</span>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.trainingRequired}
                        onChange={(e) => handleInputChange('trainingRequired', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-xs">Training Required</span>
                    </div>
                  </div>
                </div>

                {/* Row 6: Authorization */}
                <div className="border-b border-black p-2">
                  <div className="text-xs font-semibold mb-2">9. AUTHORIZATION</div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs mb-1">a. AUTHORIZED SIGNATURE *</div>
                      <input
                        value={formData.authorizedSignature}
                        onChange={(e) => handleInputChange('authorizedSignature', e.target.value)}
                        required
                        className="h-6 w-full text-xs border border-gray-300 p-1 bg-yellow-50"
                        placeholder="Type your full name"
                      />
                    </div>
                    <div>
                      <div className="text-xs mb-1">b. Position *</div>
                      <input
                        value={formData.signerTitle}
                        onChange={(e) => handleInputChange('signerTitle', e.target.value)}
                        required
                        className="h-6 w-full text-xs border border-gray-300 p-1 bg-yellow-50"
                        placeholder="Enter position"
                      />
                    </div>
                    <div>
                      <div className="text-xs mb-1">c. DATE *</div>
                      <input
                        value={formData.signatureDate}
                        onChange={(e) => handleInputChange('signatureDate', e.target.value)}
                        type="date"
                        required
                        className="h-6 w-full text-xs border border-gray-300 p-1 bg-yellow-50"
                      />
                    </div>
                  </div>
                </div>

                {/* Form Footer */}
                <div className="p-2 text-right">
                  <div className="text-xs font-semibold">PROFESSIONAL SERVICES REQUEST FORM (REV. 2024)</div>
                  <div className="text-xs">For internal use only - Confidential</div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}